import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { FileService } from '../../core/api/file.service';
import { LoadingService } from '../../core/services/loading.service';
import { HelpersService } from '../../core/services/helpers.service';
import { NotificationService } from '../../core/api/notification.service';
import { ReimburseService } from '../../core/api/reimburse.service';
import { CashAdvanceApprovalService } from '../../core/api/cash-advance-approval.service';
import { PositionService } from '../../core/api/position.service';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MatSidenavModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatExpansionModule,
    RouterModule,
    MatTooltipModule,
    MatMenuModule,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class DashboardComponent implements OnInit {
  isOpened = true;
  isMobile = false;
  today = new Date();
  currentUser: any;
  imageUrl: any;
  notificationData: any[] = [];
  isRead: boolean = true;
  pendingReimbursement: number = 0;
  pendingCashAdvance: number = 0;
  pendingSettlement: number = 0;
  positions: any[] = [];
  canAccessReportMenu: boolean = false;

  constructor(
    public authService: AuthService,
    private fileService: FileService,
    private loadingService: LoadingService,
    private notificationService: NotificationService,
    private reimburseService: ReimburseService,
    private cashAdvanceApprovalService: CashAdvanceApprovalService,
    private positionService: PositionService,
    private router: Router
  ) {
    this.currentUser = this.authService.getLoggedUser();
    if (this.currentUser.file) {
      this.loadingService.startLoading();
      this.fileService.download(this.currentUser.file.id).subscribe(
        (res: any) => {
          this.loadingService.endLoading();
          this.imageUrl = URL.createObjectURL(res);
        },
        (error) => {
          this.loadingService.endLoading();
        }
      );
    }
    this.getNotification();
    this.loadReportAccess();
  }

  ngOnInit(): void {
    this.syncSidenavWithViewport();
    this.loadPendingApprovalBadge();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.syncSidenavWithViewport();
  }

  private syncSidenavWithViewport(): void {
    const mobile = window.innerWidth < 1024;
    if (mobile !== this.isMobile) {
      this.isMobile = mobile;
      this.isOpened = !this.isMobile;
    }
  }

  getNotification() {
    this.notificationService.getAll({ limit: 10, page: 1 }).then((data) => {
      this.notificationData = data ?? [];
      this.isRead = this.notificationData.every((item: any) => item.isRead);
    });
  }

  logout() {
    this.authService.logout();
  }

  readNotification(data: any) {
    this.notificationService.readNotification(data.id).then(() => {
      this.notificationData = this.notificationData.map((item: any) =>
        item.id === data.id ? { ...item, isRead: true } : item
      );
      this.isRead = this.notificationData.every((item: any) => item.isRead);
      if (data.type == 'REIMBURSE') {
        this.router.navigate(['/dashboard/reimbursement-approval/action'], {
          queryParams: {
            m: 'detail',
            id: data.referTo,
          },
        });
      } else if (data.type == 'CASH_ADVANCE') {
        this.router.navigate(['/dashboard/cash-advance-approval/action'], {
          queryParams: {
            m: 'detail',
            id: data.referTo,
          },
        });
      } else if (data.type == 'CASH_ADVANCE_SETTLEMENT') {
        this.router.navigate(['/dashboard/cash-advance-settlement/action'], {
          queryParams: {
            m: 'detail',
            id: data.referTo,
          },
        });
      } else if (data.type == 'CASH_ADVANCE_FINANCE') {
        this.router.navigate(['/dashboard/cash-advance-finance/action'], {
          queryParams: {
            m: 'detail',
            id: data.referTo,
          },
        });
      } else {
        this.router.navigate(['/dashboard'])
      }
    });
  }

  readAllNotification() {
    this.notificationService.readAllNotification(this.currentUser.id).then(() => {
      this.notificationData = this.notificationData.map((item: any) => ({
        ...item,
        isRead: true,
      }));
      this.isRead = true;
    });
  }

  getNotificationIcon(type?: string): string {
    switch (type) {
      case 'REIMBURSE':
        return 'fa-clipboard-list';
      case 'CASH_ADVANCE':
        return 'fa-credit-card';
      case 'CASH_ADVANCE_SETTLEMENT':
        return 'fa-wallet';
      case 'CASH_ADVANCE_FINANCE':
        return 'fa-building-columns';
      default:
        return 'fa-bell';
    }
  }

  getNotificationIconClass(type?: string): string {
    switch (type) {
      case 'REIMBURSE':
        return 'notif-icon-reimburse';
      case 'CASH_ADVANCE':
        return 'notif-icon-cash';
      case 'CASH_ADVANCE_SETTLEMENT':
        return 'notif-icon-settlement';
      case 'CASH_ADVANCE_FINANCE':
        return 'notif-icon-finance';
      default:
        return 'notif-icon-default';
    }
  }

  private loadPendingApprovalBadge() {
    if (
      !(
        this.authService.isApprover(this.currentUser) ||
        this.authService.isSysAdmin(this.currentUser)
      )
    ) {
      return;
    }

    this.reimburseService
      .getCountStatusApproval()
      .then((res: any) => {
        this.pendingReimbursement = Number(res?.sent ?? 0);
      })
      .catch(() => {
        this.pendingReimbursement = 0;
      });

    this.cashAdvanceApprovalService
      .getAllApproval({
        page: 1,
        limit: 1,
        status: 'received',
      } as any)
      .then((res: any) => {
        this.pendingCashAdvance = Number(res?.receivedCount ?? 0);
      })
      .catch(() => {
        this.pendingCashAdvance = 0;
      });

    this.cashAdvanceApprovalService
      .getAllVerification({
        page: 1,
        limit: 1,
        status: 'received',
      } as any)
      .then((res: any) => {
        this.pendingSettlement = Number(res?.receivedCount ?? 0);
      })
      .catch(() => {
        this.pendingSettlement = 0;
      });
  }

  private loadReportAccess(): void {
    this.positionService
      .getAllData()
      .then((res) => {
        this.positions = res;
        this.canAccessReportMenu = this.authService.canAccessReport(
          this.currentUser,
          this.positions
        );
      })
      .catch(() => {
        this.canAccessReportMenu = this.authService.canAccessReport(
          this.currentUser
        );
      });
  }

  getUserMeta(): string {
    const role = this.currentUser?.role?.name;
    const team = this.currentUser?.team?.name;
    if (role && team) return `${role} · ${team}`;
    return role || team || '';
  }
}
